<script lang="ts" setup>
import { computed, onBeforeMount, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useForm, useField } from 'vee-validate'
import { object, string } from 'yup'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollStore } from '@/store/payroll-data'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import { DBtn, DLoading, DTextAreaField, DTextField } from 'front-library'

import {
  createMasterConceptGroup,
  getMasterConceptGroup,
  updateMasterConceptGroup
} from '@/app/core/services/master-concept-groups'

const route = useRoute()
const router = useRouter()
const notifications = useNotificationsStore()
const payrollStore = usePayrollStore()
const { t } = useI18n()

usePayrollChange(() =>
  Boolean(route.params.id) ? back() : window.location.reload()
)

const loading = ref(false)
const messageLoading = ref('')

const isEdit = computed(() => Boolean(route.params.id))

const validationSchema = computed(() =>
  object().shape({
    code: isEdit.value
      ? string().optional()
      : string()
          .required(t('form.valid.required'))
          .min(1, t('form.valid.minLength', [1]))
          .max(255, t('form.valid.maxLength', [255])),
    description: string()
      .required(t('form.valid.required'))
      .min(3, t('form.valid.minLength', [3]))
      .max(255, t('form.valid.maxLength', [255]))
  })
)

const { handleSubmit, errors, setValues } = useForm({
  validationSchema
})

const { value: code } = useField<string>('code')
const { value: description } = useField<string>('description')

const countryName = computed(() => payrollStore.payroll?.country?.name || '')
const countryId = computed(() => payrollStore.payroll?.country?.id || '')

const back = () => {
  void router.push({ name: 'ListMasterConceptGroups' })
}

const getEntity = async () => {
  if (!isEdit.value) return

  const model = await getMasterConceptGroup(route.params.id as string)
  if (!model) {
    notifications.addNotification({
      title: t('notification.notFound'),
      text: 'Master concept group not found',
      type: 'error',
      time: 10000
    })
    back()
    return
  }

  setValues({
    code: model.code,
    description: model.description
  })
}

const onSubmit = handleSubmit(async values => {
  loading.value = true
  try {
    let saved = false

    if (!isEdit.value) {
      saved = await createMasterConceptGroup({
        code: values.code,
        description: values.description,
        countryId: countryId.value
      })
    } else {
      saved = await updateMasterConceptGroup(
        {
          description: values.description
        },
        route.params.id as string
      )
    }

    if (saved) {
      notifications.addNotification({
        title: !isEdit.value
          ? t('notification.created')
          : t('notification.edited'),
        text: !isEdit.value
          ? 'Master concept group was created'
          : 'Master concept group was updated',
        type: 'success',
        time: 10000
      })

      void router.push({ name: 'ListMasterConceptGroups' })
    }
  } catch (error: any) {
    notificationError(error, 'masterConceptGroups', t, notifications, loading)
  } finally {
    loading.value = false
  }
})

onBeforeMount(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')

  try {
    await getEntity()
  } catch (error: any) {
    notificationError(error, 'masterConceptGroups', t, notifications, loading)
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <DLoading v-show="loading" :message="messageLoading" />

  <div class="h-full px-5">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p class="text-3xl font-semibold text-shadow text-blue-900">
          {{
            isEdit ? 'Editar Grupo de Conceptos' : 'Crear Grupo de Conceptos'
          }}
        </p>
      </div>
    </div>

    <form class="pt-responsive" @submit.prevent="onSubmit">
      <div
        class="grid grid-cols-8 px-6 py-4 bg-white rounded-md shadow-md gap-4">
        <DTextField
          class="col-span-8 sm:col-span-4"
          :label="'Código'"
          :placeholder="'Ingrese código, se permite mayúscula y guiones bajos'"
          :error="errors.code"
          :required="!isEdit"
          :readonly="isEdit"
          v-model="code"
          :rules="{
            pattern: {
              value: /^[A-Z_]+$/,
              message: 'Solo se permiten mayúsculas y guiones bajos'
            }
          }" />

        <div class="col-span-8 sm:col-span-4">
          <p class="text-sm font-semibold text-gray-700 mb-1">Country</p>
          <div
            class="h-10 px-3 rounded-md border border-gray-300 bg-gray-100 flex items-center">
            {{ countryName }}
          </div>
        </div>

        <DTextAreaField
          class="col-span-8"
          :label="'Descripción'"
          :placeholder="'Ingrese descripción'"
          :error="errors.description"
          :required="true"
          v-model="description" />
      </div>

      <div class="p-5 bg-white shadow-md h-24 rounded-br-md rounded-bl-md mt-2">
        <hr class="pb-5" />
        <div class="flex justify-end gap-2 items-center">
          <DBtn
            type="button"
            class="font-semibold py-1 text-base"
            color="secondary"
            @click="back()">
            {{ t('form.button.cancel') }}
          </DBtn>
          <DBtn
            class="font-semibold py-1 text-base"
            color="blue"
            @click="onSubmit">
            {{ isEdit ? t('form.button.update') : t('form.button.save') }}
          </DBtn>
        </div>
      </div>
    </form>
  </div>
</template>
